/**
 * Код входа и защита локального кэша.
 *
 * Две задачи, которые решаются одним ключом:
 *
 * 1. Впустить в приложение без связи. Сервер в подвале недоступен, а войти
 *    надо — иначе офлайн-режим бессмысленен. Поэтому рядом с кодом лежит его
 *    проверочная запись, и она проверяется на устройстве.
 * 2. Не отдать пароли клиентов вместе с телефоном. Кэш доступов шифруется
 *    ключом, выведенным из кода: без ввода кода расшифровать его нечем.
 *
 * Ключ выводится PBKDF2 с большим числом итераций. Это не столько про
 * стойкость шифра, сколько про скорость перебора: пятизначный код сам по себе
 * подбирается мгновенно, и единственное, что делает перебор дорогим, — цена
 * одной попытки.
 */

const SALT_KEY = 'vault-salt'
const CHECK_KEY = 'vault-check'
const FAILS_KEY = 'vault-fails'

/** Столько итераций телефон считает примерно четверть секунды — незаметно при
 *  входе и мучительно при переборе десяти тысяч вариантов. */
const ITERATIONS = 210_000

/** Известная строка: расшифровалась — код верный. */
const CHECK_PHRASE = 'timetrack-vault-v1'

/** Ключ живёт только в памяти. Перезапуск приложения требует ввода кода —
 *  это и есть смысл замка. */
let sessionKey: CryptoKey | null = null

function b64(bytes: ArrayBuffer | Uint8Array): string {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let s = ''
  for (const x of b) s += String.fromCharCode(x)
  return btoa(s)
}

function unb64(s: string): Uint8Array {
  const raw = atob(s)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* приватный режим — замок просто не заведётся */
  }
}

async function derive(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations: ITERATIONS, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function seal(key: CryptoKey, plain: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plain),
  )
  return `${b64(iv)}.${b64(data)}`
}

async function open(key: CryptoKey, blob: string): Promise<string> {
  const [iv, data] = blob.split('.')
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: unb64(iv) as unknown as BufferSource },
    key,
    unb64(data) as unknown as BufferSource,
  )
  return new TextDecoder().decode(plain)
}

/** Есть ли на устройстве заведённый код. */
export function vaultReady(): boolean {
  return !!read(SALT_KEY) && !!read(CHECK_KEY)
}

/** Заводит код на устройстве. Вызывается после обычного входа. */
export async function setLocalPin(pin: string): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const key = await derive(pin, salt)
  write(SALT_KEY, b64(salt))
  write(CHECK_KEY, await seal(key, CHECK_PHRASE))
  write(FAILS_KEY, '0')
  sessionKey = key
}

/**
 * Проверяет код на устройстве. Возвращает, подошёл ли он.
 *
 * Счётчик неудач держится здесь же: при отсутствии связи серверный счётчик
 * недоступен, а без ограничения пятизначный код подбирается за вечер.
 */
export async function unlockLocal(pin: string): Promise<boolean> {
  const salt = read(SALT_KEY)
  const check = read(CHECK_KEY)
  if (!salt || !check) return false
  try {
    const key = await derive(pin, unb64(salt))
    const phrase = await open(key, check)
    if (phrase !== CHECK_PHRASE) return false
    sessionKey = key
    write(FAILS_KEY, '0')
    return true
  } catch {
    return false
  }
}

/** Сколько раз подряд ввели неверно. */
export function localFails(): number {
  return Number(read(FAILS_KEY) ?? '0') || 0
}

export function registerLocalFail(): number {
  const n = localFails() + 1
  write(FAILS_KEY, String(n))
  return n
}

/** Забыть код: после выхода и после исчерпания попыток. */
export function forgetLocalPin(): void {
  sessionKey = null
  write(SALT_KEY, null)
  write(CHECK_KEY, null)
  write(FAILS_KEY, null)
}

/**
 * Зашифровать значение для локального хранения.
 *
 * Если код не заведён, возвращаем как есть: приложение и так открывается без
 * спроса по сохранённому токену, и шифровать было бы нечем — ключ взять
 * неоткуда. Защита появляется вместе с кодом, а не притворяется заранее.
 */
export async function protect(value: unknown): Promise<unknown> {
  if (!sessionKey) return value
  return { __sealed: await seal(sessionKey, JSON.stringify(value)) }
}

/** Расшифровать то, что вернул protect. */
export async function reveal<T>(stored: unknown, fallback: T): Promise<T> {
  if (stored === undefined || stored === null) return fallback
  if (typeof stored !== 'object' || !('__sealed' in (stored as object))) return stored as T
  if (!sessionKey) return fallback // код не введён — данных как будто нет
  try {
    return JSON.parse(await open(sessionKey, (stored as { __sealed: string }).__sealed)) as T
  } catch {
    return fallback
  }
}

/** Заведён ли ключ в этом запуске: без него зашифрованный кэш не прочитать. */
export function unlocked(): boolean {
  return sessionKey !== null
}
